import { create } from 'zustand';

import { send } from '@/api/send';
import type { BudgetMonth } from '@/api/types';
import { addMonthLength, currentMonth } from '@/utils/months';

type BudgetState = {
  bounds: { min: string; max: string } | null;
  startMonth: string;
  monthCount: number;
  collapsedGroups: string[];
  months: BudgetMonth[];
  loading: boolean;
  error: string | null;
  setMonthCount: (count: number) => void;
  shiftMonth: (delta: number) => void;
  jumpToMonth: (month: string) => void;
  toggleGroup: (id: string) => void;
  load: () => Promise<void>;
  setBudget: (month: string, categoryId: string, amount: number) => Promise<void>;
};

const MAX_VISIBLE_MONTHS = 16;

export const useBudgetStore = create<BudgetState>((set, get) => ({
  bounds: null,
  startMonth: currentMonth(),
  monthCount: 2,
  collapsedGroups: [],
  months: [],
  loading: false,
  error: null,

  setMonthCount(count) {
    set({ monthCount: Math.min(MAX_VISIBLE_MONTHS, Math.max(1, count)) });
    void get().load();
  },

  shiftMonth(delta) {
    const { bounds, startMonth, monthCount } = get();
    const next = addMonthLength(startMonth, delta);
    if (bounds && (next < bounds.min || addMonthLength(next, monthCount - 1) > bounds.max)) {
      return;
    }
    set({ startMonth: next });
    void get().load();
  },

  jumpToMonth(month) {
    set({ startMonth: month });
    void get().load();
  },

  toggleGroup(id) {
    set(state => ({
      collapsedGroups: state.collapsedGroups.includes(id)
        ? state.collapsedGroups.filter(item => item !== id)
        : [...state.collapsedGroups, id],
    }));
  },

  async load() {
    const { startMonth, monthCount } = get();
    set({ loading: true, error: null });
    try {
      const months: BudgetMonth[] = [];
      for (let index = 0; index < monthCount; index += 1) {
        months.push(
          await send('envelope-budget-month', {
            month: addMonthLength(startMonth, index),
          }),
        );
      }
      const bounds = get().bounds ?? (await send('get-budget-bounds'));
      set({ months, bounds, loading: false });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '预算加载失败',
      });
    }
  },

  async setBudget(month, categoryId, amount) {
    await send('envelope-budget-set-month', { month, category: categoryId, amount });
    const index = get().months.findIndex(item => item.month === month);
    if (index >= 0) {
      const refreshed = await send('envelope-budget-month', { month });
      const months = [...get().months];
      months[index] = refreshed;
      set({ months });
    }
    void send('save-prefs', { diff: { lastBudgetMonth: month } });
  },
}));
